import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fieldLibraryApi,
  type CreateLibraryFieldPayload,
  type FieldLibraryListParams,
  type FieldLibraryListResult,
  type UpdateLibraryFieldPayload,
} from '@/api/fieldLibraryApi'
import type { LibraryFieldImportRow } from '@/lib/formExcel'
import { toCellLibraryFields, type CellLibraryField } from '@/lib/tableCellLibrary'

/** Prefix shared by every cached list query, regardless of filter/page params. */
const FIELD_LIBRARY_LIST_KEY = ['fieldLibrary', 'list'] as const

export type { FieldLibraryListParams }

/** Prefix key used for invalidating all Field Library queries (list + usage). */
const FIELD_LIBRARY_KEY = ['fieldLibrary'] as const

/** List query + create/update/delete mutations for the Field Library. */
export function useFieldLibrary(params: FieldLibraryListParams = {}) {
  const qc = useQueryClient()

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: FIELD_LIBRARY_KEY })
  }

  const list = useQuery({
    queryKey: ['fieldLibrary', 'list', params],
    queryFn: () => fieldLibraryApi.listFields(params),
    placeholderData: keepPreviousData,
  })

  const create = useMutation({
    mutationFn: (payload: CreateLibraryFieldPayload) => fieldLibraryApi.createField(payload),
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: (input: { id: string; payload: UpdateLibraryFieldPayload }) =>
      fieldLibraryApi.updateField(input.id, input.payload),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (id: string) => fieldLibraryApi.deleteField(id),
    // onSettled, not onSuccess: ProcessPage's delete treats a 404 as "already
    // gone" and proceeds, yet the mutation still rejects — so the library list
    // kept showing a field the server had already dropped. A refetch after a
    // real failure costs one read and re-syncs either way.
    onSettled: invalidate,
  })

  const importFields = useMutation({
    mutationFn: (rows: LibraryFieldImportRow[]) => fieldLibraryApi.importFields(rows),
    onSuccess: () => {
      invalidate()
      // Import auto-creates categories named in the file, so refresh those too.
      void qc.invalidateQueries({ queryKey: ['fieldCategories'] })
    },
  })

  // Dedicated optimistic mutation for the Field Library table's row-level Enabled
  // switch — flips the value in every cached list snapshot immediately (before the
  // PUT resolves) so the UI doesn't wait on a network round trip to feel responsive.
  // Sends only `{ enabled }`, not a full reconstructed payload — the backend PUT is
  // a partial merge, so nothing else needs to be present in the body.
  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      fieldLibraryApi.updateField(id, { enabled }),
    onMutate: async ({ id, enabled }) => {
      await qc.cancelQueries({ queryKey: FIELD_LIBRARY_LIST_KEY })
      const previous = qc.getQueriesData<FieldLibraryListResult>({ queryKey: FIELD_LIBRARY_LIST_KEY })
      qc.setQueriesData<FieldLibraryListResult>({ queryKey: FIELD_LIBRARY_LIST_KEY }, old =>
        old
          ? { ...old, items: old.items.map(f => (f.id === id ? { ...f, enabled } : f)) }
          : old,
      )
      return { previous }
    },
    onError: (_err, _vars, context) => {
      context?.previous.forEach(([key, data]) => qc.setQueryData(key, data))
    },
    // Reconcile with the server once it responds — picks up the row leaving/entering
    // the current filter's result set, corrected totals/typeCounts, etc.
    onSettled: invalidate,
  })

  return { list, create, update, remove, importFields, toggleEnabled }
}

/** Read-only list query — use in contexts that do not need mutations (e.g. pickers). */
export function useFieldLibraryList(params: FieldLibraryListParams = {}) {
  return useQuery({
    queryKey: ['fieldLibrary', 'list', params],
    queryFn: () => fieldLibraryApi.listFields(params),
    placeholderData: keepPreviousData,
  })
}

/**
 * The Field Library fields a table cell can be linked to — every field whose
 * type a cell can hold, exactly as the palette offers fields for a form.
 *
 * Under the `['fieldLibrary']` prefix, so an import or an edit in the Field
 * Library refreshes the picker with every other library query.
 */
export function useCellLibraryFields(enabled = true) {
  return useQuery({
    queryKey: ['fieldLibrary', 'cell-fields'],
    queryFn: async (): Promise<CellLibraryField[]> =>
      toCellLibraryFields(await fieldLibraryApi.listAllFields()),
    enabled,
  })
}

/**
 * The process steps a picker can filter by.
 *
 * `staleTime: 0` on purpose: the list is derived from saved forms, and nothing
 * on the form-save path invalidates `['fieldLibrary']`. An author who adds a
 * Process field, saves, and opens the picker should see the new step, not the
 * five-minute-old default cache.
 */
export function useProcessStepOptions(enabled = true) {
  return useQuery({
    queryKey: ['fieldLibrary', 'processSteps'],
    queryFn: () => fieldLibraryApi.listProcessSteps(),
    staleTime: 0,
    enabled,
  })
}

/** Usage query for a single library field — which forms have copied it. */
export function useFieldUsage(id: string | null) {
  return useQuery({
    queryKey: ['fieldLibrary', id, 'usage'],
    queryFn: () => fieldLibraryApi.getUsage(id as string),
    enabled: !!id,
  })
}
